<template>
  <div class="modal-products-operations">
    <h3 class="operations-title">Toplu İşlemler</h3>
    <CurrencyInputLabel
      v-model="bulkOperationsData.listPrice"
      color="unset"
      :options="currencyInputOptions"
      :class="['operations-input', { isErrorTooltip: listPriceErrorMessage }]"
      id="market-selling-price"
      label="Piyasa Satış Fiyatı"
      :disableAutoFocus="true"
    >
      <template #right-icon>
        <div v-if="listPriceErrorMessage" class="operations-input-icon">
          <TooltipPlugin :tooltipText="listPriceErrorMessage">
            <template #tooltip-btn>
              <img src="@/assets/images/icons/alert-circle.svg" />
            </template>
          </TooltipPlugin>
        </div>
      </template>
    </CurrencyInputLabel>

    <CurrencyInputLabel
      v-model="bulkOperationsData.salePrice"
      color="unset"
      :options="currencyInputOptions"
      :class="['operations-input', { isErrorTooltip: salePriceErrorMessage }]"
      id="n11-seller-price"
      label="n11 Satış Fiyatı"
      :disableAutoFocus="true"
    >
      <template #right-icon>
        <div v-if="salePriceErrorMessage" class="operations-input-icon">
          <TooltipPlugin :tooltipText="salePriceErrorMessage">
            <template #tooltip-btn>
              <img src="@/assets/images/icons/alert-circle.svg" />
            </template>
          </TooltipPlugin>
        </div>
      </template>
    </CurrencyInputLabel>

    <Input
      type="number"
      :class="['operations-input', stockError]"
      v-model:value="bulkOperationsData.stock"
      id="stock"
      label="Stok"
      :max="maxStock"
      :min="minStock"
      :style="{ width: '100px' }"
      :inputWidth="'auto'"
    />
    <Input
      type="number"
      :class="['operations-input', durationError]"
      v-model:value="bulkOperationsData.duration"
      id="duration"
      label="Hazırlık Süresi"
      :min="1"
      :style="{ width: '120px' }"
      :inputWidth="'auto'"
      @blur="
        validatePreparingTime(props.categoryId, bulkOperationsData.duration)
      "
    />
    <NativeSelect
      :options="VAT_RATES"
      :class="['operations-input', vatError]"
      v-model:value="bulkOperationsData.vatRate"
      id="vatRate"
      label="KDV Oranı"
      :style="{ width: '96px' }"
    />
    <Button
      primary
      class="applyBtn"
      :disabled="bulkOperationsBtnDisabled"
      @click="$emit('applySelected', true)"
    >
      Seçilenlere Uygula
      <template v-if="selectedRowsLength !== 0">
        ({{ selectedRowsLength }})
      </template>
    </Button>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import {
  Button,
  Input,
  TooltipPlugin,
  NativeSelect,
} from "@n11/so-ui-components";
import CurrencyInputLabel from "@/components/DataTable/CurrencyInputLabel.vue";
import { VAT_RATES } from "@/utils/enum/tableColumns";

const emit = defineEmits(["applySelected"]);

const props = defineProps({
  selectedRowsLength: {
    type: Number,
    required: false,
    default: 0,
  },
  categoryId: {
    type: Number,
    required: false,
    default: null,
  },
});

const store = useStore();
const minStock = ref(0);
const maxStock = ref(99999);
const durationErrorMessage = ref("");
const bulkOperationsData = ref({
  listPrice: null,
  salePrice: null,
  stock: null,
  duration: null,
  vatRate: null,
});

const currencyInputOptions = computed(() => {
  return {
    locale: "tr-TR",
    currency: "TRY",
    currencyDisplay: "symbol",
    hideGroupingSeparatorOnFocus: false,
    hideNegligibleDecimalDigitsOnFocus: false,
    autoDecimalDigits: false,
    useGrouping: true,
    accountingSign: false,
    valueRange: { min: 0 },
  };
});

const stockError = computed(() =>
  Number(bulkOperationsData.value.stock) < 0 ||
  Number(bulkOperationsData.value.stock) > maxStock.value
    ? "redError"
    : ""
);

const durationError = computed(() =>
  Number(bulkOperationsData.value.duration) < 0 ||
  bulkOperationsData.value.duration === "0" ||
  durationErrorMessage.value !== ""
    ? "redError"
    : ""
);

const bulkOperationsBtnDisabled = computed(() => {
  return (
    (!bulkOperationsData.value.listPrice &&
      !bulkOperationsData.value.salePrice &&
      !bulkOperationsData.value.stock &&
      !bulkOperationsData.value.duration &&
      !bulkOperationsData.value.vatRate) ||
    props.selectedRowsLength === 0 ||
    Number(bulkOperationsData.value.duration) < 0 ||
    bulkOperationsData.value.duration === "0" ||
    durationErrorMessage.value !== "" ||
    Number(bulkOperationsData.value.stock) < 0 ||
    Number(bulkOperationsData.value.stock) > maxStock.value ||
    bulkOperationsData.value.listPrice < bulkOperationsData.value.salePrice ||
    bulkOperationsData.value.vatRate === ""
  );
});

const listPriceErrorMessage = computed(() => {
  if (
    !bulkOperationsData.value.listPrice &&
    bulkOperationsData.value.salePrice
  ) {
    return "Piyasa Satış Fiyatı alanı boş bırakılamaz.";
  }
  if (
    bulkOperationsData.value.listPrice &&
    bulkOperationsData.value.salePrice &&
    bulkOperationsData.value.listPrice < bulkOperationsData.value.salePrice
  ) {
    return "Piyasa Satış Fiyatı, n11 Satış Fiyatı’ndan düşük olmamalıdır.";
  }
  return null;
});

const salePriceErrorMessage = computed(() => {
  if (
    !bulkOperationsData.value.salePrice &&
    bulkOperationsData.value.listPrice
  ) {
    return "n11 Satış Fiyatı alanı boş bırakılamaz.";
  }
  return null;
});

const validatePreparingTime = async (categoryId, day) => {
  if (!day) {
    durationErrorMessage.value = "";
    return;
  }
  try {
    await store.dispatch("validations/validatePreparingTime", {
      day,
      id: categoryId,
    });
    durationErrorMessage.value = "";
  } catch (e) {
    bulkOperationsData.value.duration = day;
    durationErrorMessage.value =
      e?.data?.message || "Hazırlık süresi zorunludur.";
    store.commit("common/SET_TOASTER_DATA", {
      type: "error",
      title: "",
      description: durationErrorMessage.value,
    });
  }
};

defineExpose({
  bulkOperationsData,
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
.modal-products-operations {
  display: flex;
  align-items: center;
  gap: 16px;
  background-color: $colorN10;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  position: relative;

  .operations-title {
    font-size: 16px;
    font-weight: 700;
    white-space: nowrap;
  }

  // Combine selectors with the base class *inside* :deep()
  :deep(.operations-input.redError),
  :deep(.operations-input.isErrorTooltip) {
    border-color: red;
    outline: 3px solid $colorE10;

    &:hover {
      border-color: red;
    }
  }

  // Use a descendant selector (space) for child elements
  :deep(.operations-input) .operations-input-icon {
    width: 18px;
    height: 18px;
    position: absolute;
    z-index: 1;
    right: 12px;
    top: 11px;
    cursor: pointer;
  }

  .applyBtn {
    min-width: 171px;
    width: 171px;
    font-size: 14px;

    &.disabled {
      background-color: $colorN40;
    }
  }
}

</style>
